import { useState, memo } from "react";
import { SessionCheckpoint, Checkpoint } from "./SessionCheckpoint";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Button } from "@/components/ui/button";
import { Plus, Clock, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";

const MOCK_CHECKPOINTS: Checkpoint[] = [
  {
    id: "1",
    name: "Sunset Groove",
    timestamp: new Date(Date.now() - 1000 * 60 * 5),
    prompt: "Funky jazz fusion with electronic textures",
    isCurrent: true,
  },
  {
    id: "2",
    name: "Morning Chill",
    timestamp: new Date(Date.now() - 1000 * 60 * 30),
    prompt: "Ambient chill vibes with soft pads",
  },
  {
    id: "3",
    name: "Dance Floor",
    timestamp: new Date(Date.now() - 1000 * 60 * 60),
    prompt: "Upbeat house music like Daft Punk",
  },
];

export const SessionPanel = memo(function SessionPanel() {
  const [checkpoints, setCheckpoints] = useState<Checkpoint[]>(MOCK_CHECKPOINTS);
  const [isExpanded, setIsExpanded] = useState(true);

  const handleLoad = (id: string) => {
    setCheckpoints((prev) =>
      prev.map((cp) => ({ ...cp, isCurrent: cp.id === id }))
    );
  };

  const handleFork = (id: string) => {
    const source = checkpoints.find((cp) => cp.id === id);
    if (source) {
      const newCheckpoint: Checkpoint = {
        id: Date.now().toString(),
        name: `${source.name} (Fork)`,
        timestamp: new Date(),
        prompt: source.prompt,
        isCurrent: true,
      };
      setCheckpoints((prev) => [
        newCheckpoint,
        ...prev.map((cp) => ({ ...cp, isCurrent: false })),
      ]);
    }
  };

  const handleCreateCheckpoint = () => {
    const newCheckpoint: Checkpoint = {
      id: Date.now().toString(),
      name: `Checkpoint ${checkpoints.length + 1}`,
      timestamp: new Date(),
      prompt: "Manual checkpoint from the current listening session",
      isCurrent: true,
    };
    setCheckpoints((prev) => [
      newCheckpoint,
      ...prev.map((cp) => ({ ...cp, isCurrent: false })),
    ]);
  };

  return (
    <div className="border-t border-white/5 bg-card/20 backdrop-blur-md">
      {/* Header - Collapsible */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full flex items-center justify-between px-6 py-4 hover:bg-white/5 transition-colors group"
      >
        <div className="flex items-center gap-3">
          <Clock className="w-4 h-4 text-muted-foreground group-hover:text-accent transition-colors" />
          <span className="text-[10px] font-bold uppercase tracking-widest text-foreground">Session History</span>
          <span className="px-1.5 py-0.5 text-[10px] font-bold rounded-md bg-white/5 text-muted-foreground border border-white/5">
            {checkpoints.length}
          </span>
        </div>
        <ChevronDown
          className={cn(
            "w-4 h-4 text-muted-foreground transition-transform duration-300",
            isExpanded && "rotate-180"
          )}
        />
      </button>

      {/* Content */}
      <div
        className={cn(
          "overflow-hidden transition-all duration-500 ease-in-out",
          isExpanded ? "max-h-[400px] opacity-100" : "max-h-0 opacity-0"
        )}
      >
        <div className="px-6 pb-6 space-y-4">
          <Button
            variant="glass"
            size="sm"
            className="w-full gap-2 rounded-xl border-white/5 bg-white/5 hover:bg-white/10"
            onClick={handleCreateCheckpoint}
          >
            <Plus className="w-4 h-4" />
            New Checkpoint
          </Button>

          <ScrollArea className="h-[240px] -mx-2 px-2">
            <div className="space-y-3">
              {checkpoints.map((checkpoint) => (
                <SessionCheckpoint
                  key={checkpoint.id}
                  checkpoint={checkpoint}
                  onLoad={handleLoad}
                  onFork={handleFork}
                />
              ))}
            </div>
          </ScrollArea>
        </div>
      </div>
    </div>
  );
});
